import { useEffect, useState } from 'react'
import { authJsonHeaders } from './incentives'

declare let ORDERS_SVC_API5: string

export interface OrderItemSnapshot {
  name: string
  price: number
}

export interface OrderOffer {
  type?: string
  title?: string
  reasoning?: string
}

export interface OrderIncentiveSnapshot {
  discount: string | null
  from_order_uuid: string | null
  offer: string | OrderOffer | null
}

export interface OrderSnapshot {
  total: number
  items: OrderItemSnapshot[]
  day_of_week: string
  order_type: string
  incentive: OrderIncentiveSnapshot | null
  time_of_day: string
  gap_from_previous_order: number
  loyalty_points_earned: number
  loyalty_points_redeemed: number
  sent_offers: any[]
}

export interface PastOrdersResponse {
  current_order: OrderSnapshot | null
  past_orders: OrderSnapshot[]
}

export interface LoyaltyReward {
  name: string
  value: number
  points_cost: number
}

export interface CustomerLoyalty {
  highest_reward_available: LoyaltyReward | null
  average_points_earned_per_order: number
  life_time_points: number
  next_reward: LoyaltyReward | null
  points_balance: number
  points_earned_on_order: number
  points_redeemed_on_order: number
  points_to_next_reward: number | null
}

export interface CustomerProfile {
  customer_aov: number
  order_count: number
  restaurant_aov: number
  loyalty: CustomerLoyalty | null
  customer_type: string
}

export interface CustomerMarketingResult {
  profile: CustomerProfile | null
  past: PastOrdersResponse | null
  loading: boolean
}

export interface ReorderDueInfo {
  reorder_due_days: number
  reorder_due_time: string
}

export interface OrderAgainTriggerStatus {
  already_placed_order: boolean
  manually_triggered_at: string
}

function loadProfile(orderUuid: string): Promise<CustomerProfile | null> {
  return fetch(`${ORDERS_SVC_API5}/v5/internal/orders/${orderUuid}/customer_profile`, {
    method: 'GET',
    headers: authJsonHeaders(),
  })
    .then((res: Response) => (res.ok ? res.json() : null))
    .then((data: any) => (data && typeof data.order_count === 'number' ? data : null))
    .catch(() => null)
}

function loadPastOrders(orderUuid: string): Promise<PastOrdersResponse | null> {
  return fetch(`${ORDERS_SVC_API5}/v5/internal/orders/${orderUuid}/past_orders`, {
    method: 'GET',
    headers: authJsonHeaders(),
  })
    .then((res: Response) => (res.ok ? res.json() : null))
    .then((data: any) => (data && Array.isArray(data.past_orders) ? data : null))
    .catch(() => null)
}

export function loadReorderDue(orderUuid: string): Promise<ReorderDueInfo | null> {
  return fetch(`${ORDERS_SVC_API5}/v5/orders/${orderUuid}/reorder_due`, {
    method: 'GET',
    headers: authJsonHeaders(),
  })
    .then((res: Response) => (res.ok ? res.json() : null))
    .then((data: any) => (data && typeof data.reorder_due_time === 'string' ? data : null))
    .catch(() => null)
}

export function triggerOrderAgain(orderUuid: string): Promise<OrderAgainTriggerStatus | null> {
  return fetch(`${ORDERS_SVC_API5}/v5/orders/${orderUuid}/order_again/trigger`, {
    method: 'POST',
    headers: authJsonHeaders(),
  })
    .then((res: Response) => (res.ok ? res.json() : null))
    .then((data: any) => (data && typeof data.manually_triggered_at === 'string' ? data : null))
    .catch(() => null)
}

export function useCustomerMarketing(orderUuid: string): CustomerMarketingResult {
  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  const [past, setPast] = useState<PastOrdersResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    const p1 = loadProfile(orderUuid).then((v) => live && setProfile(v))
    const p2 = loadPastOrders(orderUuid).then((v) => live && setPast(v))
    Promise.all([p1, p2]).finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [orderUuid])

  return { profile, past, loading }
}

export function hasPastOrders(p: PastOrdersResponse | null): p is PastOrdersResponse {
  return p != null && (p.current_order != null || (p.past_orders && p.past_orders.length > 0))
}

export function hasCustomerMarketingData(r: CustomerMarketingResult): boolean {
  return !r.loading && (r.profile != null || hasPastOrders(r.past))
}
